import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, KeyRound, LockOpen, Plus, ShieldOff } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth/context";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Alert, Badge } from "@/components/ui/Feedback";
import { CheckboxField, SelectField, TextField } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { adminApi, type AdminUser, type AdminUserDetail, type ScopeRule } from "@/lib/admin-api";
import { ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { reloadAt } from "@/lib/reload";

import { ScopeRulesEditor } from "./ScopeRulesEditor";
import { TemporaryPasswordDialog } from "./TemporaryPasswordDialog";

function useErrorText() {
  const { t } = useTranslation();
  return (error: unknown) => {
    if (error instanceof ApiError && error.code) {
      const key = `admin.users.errors.${error.code}`;
      const text = t(key);
      if (text !== key) return text;
    }
    return error ? t("common.errorGeneric") : null;
  };
}

function StatusBadges({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-1">
      {user.is_active ? (
        <Badge tone="primary">{t("admin.users.active")}</Badge>
      ) : (
        <Badge>{t("admin.users.inactive")}</Badge>
      )}
      {user.locked && <Badge tone="danger">{t("admin.users.locked")}</Badge>}
      {user.must_change_password && <Badge tone="accent">{t("admin.users.mustChange")}</Badge>}
    </div>
  );
}

function CreateUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (password: string) => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const roles = useQuery({ queryKey: ["admin", "roles"], queryFn: adminApi.roles, enabled: open });
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const create = useMutation({
    mutationFn: () =>
      adminApi.createUser({ username, display_name: displayName, email: email || null, role_ids: roleIds }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setUsername("");
      setDisplayName("");
      setEmail("");
      setRoleIds([]);
      onCreated(result.temporary_password);
    },
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => !value && onClose()}
      title={t("admin.users.new")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="create-user" busy={create.isPending}>
            {t("common.create")}
          </Button>
        </>
      }
    >
      <form id="create-user" onSubmit={onSubmit} className="space-y-4">
        {create.isError && <Alert>{errorText(create.error)}</Alert>}
        <TextField
          label={t("admin.users.username")}
          hint={t("admin.users.usernameHint")}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          pattern="[A-Za-z0-9._@\-]+"
          required
          autoFocus
        />
        <TextField
          label={t("admin.users.displayName")}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
        <TextField
          label={t("admin.users.email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium">{t("admin.users.roles")}</legend>
          {roles.data?.map((role) => (
            <CheckboxField
              key={role.id}
              label={role.name}
              description={role.description || undefined}
              checked={roleIds.includes(role.id)}
              onChange={(e) =>
                setRoleIds(e.target.checked ? [...roleIds, role.id] : roleIds.filter((id) => id !== role.id))
              }
            />
          ))}
        </fieldset>
      </form>
    </Dialog>
  );
}

type OverrideChoice = "inherit" | "grant" | "revoke";

function EditUserDialog({
  userId,
  onClose,
  onPassword,
}: {
  userId: number | null;
  onClose: () => void;
  onPassword: (password: string) => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const { me, can } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const open = userId !== null;
  const detail = useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: () => adminApi.user(userId as number),
    enabled: open,
  });
  const roles = useQuery({ queryKey: ["admin", "roles"], queryFn: adminApi.roles, enabled: open });
  const catalog = useQuery({
    queryKey: ["admin", "permissions"],
    queryFn: adminApi.permissions,
    enabled: open,
  });
  const queues = useQuery({ queryKey: ["admin", "queues"], queryFn: adminApi.queues, enabled: open });
  const [draft, setDraft] = useState<AdminUserDetail | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Start editing from freshly loaded data (React's "adjust state when data changes" pattern, no effect).
  const [loaded, setLoaded] = useState<AdminUserDetail | undefined>(undefined);
  if (detail.data && detail.data !== loaded) {
    setLoaded(detail.data);
    setDraft(detail.data);
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
  };
  const save = useMutation({
    mutationFn: (user: AdminUserDetail) =>
      adminApi.updateUser(user.id, {
        display_name: user.display_name,
        email: user.email,
        is_active: user.is_active,
        role_ids: user.roles.map((r) => r.id),
        overrides: user.overrides,
        scope_rules: user.scope_rules,
      }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const resetPassword = useMutation({
    mutationFn: () => adminApi.resetPassword(userId as number),
    onSuccess: async (result) => {
      await refresh();
      onPassword(result.temporary_password);
    },
  });
  const reset2fa = useMutation({
    mutationFn: () => adminApi.reset2fa(userId as number),
    onSuccess: async () => {
      await refresh();
      setNotice(t("admin.users.reset2faDone"));
    },
  });
  const unlock = useMutation({
    mutationFn: () => adminApi.unlock(userId as number),
    onSuccess: async () => {
      await refresh();
      setNotice(t("admin.users.unlockDone"));
    },
  });

  const viewAs = useMutation({
    mutationFn: () => adminApi.viewAs(userId as number),
    // A full reload starts the interface afresh with the other user's permissions, menus and data.
    onSuccess: () => reloadAt("/"),
  });
  const remove = useMutation({
    mutationFn: () => adminApi.deleteUser(userId as number),
    onSuccess: async () => {
      await refresh();
      close();
    },
  });

  const close = () => {
    setDraft(null);
    setLoaded(undefined);
    setNotice(null);
    setConfirmDelete(false);
    save.reset();
    remove.reset();
    onClose();
  };
  const isSelf = draft !== null && draft.id === me?.user.id;
  // An administrator's account is theirs alone: other administrators cannot change it (the server enforces it).
  const protectedAdmin = draft !== null && draft.roles.some((r) => r.is_system) && !isSelf && !me?.is_root;
  const overrideOf = (code: string): OverrideChoice =>
    draft?.overrides[code] === undefined ? "inherit" : draft.overrides[code] ? "grant" : "revoke";
  const setOverride = (code: string, choice: OverrideChoice) => {
    if (!draft) return;
    const overrides = { ...draft.overrides };
    if (choice === "inherit") delete overrides[code];
    else overrides[code] = choice === "grant";
    setDraft({ ...draft, overrides });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => !value && close()}
      title={t("admin.users.editTitle")}
      description={draft ? `${draft.display_name} (${draft.username})` : undefined}
      wide
      footer={
        <>
          {draft && can("admin.users_delete") && !isSelf && !protectedAdmin && (
            <Button variant="danger" className="mr-auto" onClick={() => setConfirmDelete(true)}>
              {t("admin.users.delete")}
            </Button>
          )}
          <Button variant="secondary" onClick={close}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => draft && save.mutate(draft)}
            busy={save.isPending}
            disabled={!draft || protectedAdmin}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      {!draft ? (
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      ) : (
        <div className="space-y-7">
          {protectedAdmin && <Alert tone="accent">{t("admin.users.adminProtected")}</Alert>}
          {[save, remove, viewAs, resetPassword, reset2fa, unlock].map(
            (action, index) => action.isError && <Alert key={index}>{errorText(action.error)}</Alert>,
          )}
          {notice && <Alert tone="primary">{notice}</Alert>}
          {confirmDelete && (
            <Alert tone="accent">
              <p>{t("admin.users.deleteConfirm", { name: draft.display_name, username: draft.username })}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="danger" onClick={() => remove.mutate()} busy={remove.isPending}>
                  {t("admin.users.deleteNow")}
                </Button>
                <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                  {t("common.cancel")}
                </Button>
              </div>
            </Alert>
          )}
          <fieldset
            disabled={protectedAdmin}
            className="m-0 min-w-0 space-y-7 border-0 p-0 disabled:opacity-60"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label={t("admin.users.displayName")}
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
              />
              <TextField
                label={t("admin.users.email")}
                type="email"
                value={draft.email ?? ""}
                onChange={(e) => setDraft({ ...draft, email: e.target.value || null })}
              />
            </div>
            <CheckboxField
              label={t("admin.users.accountActive")}
              checked={draft.is_active}
              onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
            />
            <div className="flex flex-wrap gap-2">
              {can("admin.users_view_as") && !isSelf && draft.is_active && (
                <Button variant="secondary" onClick={() => viewAs.mutate()} busy={viewAs.isPending}>
                  <Eye className="size-4" aria-hidden="true" />
                  {t("admin.users.viewAs")}
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => resetPassword.mutate()}
                busy={resetPassword.isPending}
                disabled={protectedAdmin}
              >
                <KeyRound className="size-4" aria-hidden="true" />
                {t("admin.users.resetPassword")}
              </Button>
              {draft.totp_enabled && (
                <Button variant="secondary" onClick={() => reset2fa.mutate()} busy={reset2fa.isPending}>
                  <ShieldOff className="size-4" aria-hidden="true" />
                  {t("admin.users.reset2fa")}
                </Button>
              )}
              {draft.locked && (
                <Button variant="secondary" onClick={() => unlock.mutate()} busy={unlock.isPending}>
                  <LockOpen className="size-4" aria-hidden="true" />
                  {t("admin.users.unlock")}
                </Button>
              )}
            </div>

            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-semibold">{t("admin.users.roles")}</legend>
              {roles.data?.map((role) => (
                <CheckboxField
                  key={role.id}
                  label={role.name}
                  description={
                    role.is_system && !me?.is_admin ? t("admin.users.adminRoleReserved") : undefined
                  }
                  disabled={!me?.is_admin && (role.is_system || isSelf)}
                  checked={draft.roles.some((r) => r.id === role.id)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      roles: e.target.checked
                        ? [...draft.roles, { id: role.id, name: role.name, is_system: role.is_system }]
                        : draft.roles.filter((r) => r.id !== role.id),
                    })
                  }
                />
              ))}
            </fieldset>

            <section className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold">{t("admin.users.permissionOverrides")}</h3>
                <p className="text-xs text-ink-subtle">{t("admin.users.overridesHint")}</p>
              </div>
              <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                {catalog.data?.flatMap((group) =>
                  group.permissions.map((code) => (
                    <SelectField
                      key={code}
                      label={t(`permissions.${code}`)}
                      value={overrideOf(code)}
                      onChange={(e) => setOverride(code, e.target.value as OverrideChoice)}
                    >
                      <option value="inherit">{t("admin.users.inherit")}</option>
                      <option value="grant">{t("admin.users.grant")}</option>
                      <option value="revoke">{t("admin.users.revoke")}</option>
                    </SelectField>
                  )),
                )}
              </div>
            </section>

            <section className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold">{t("admin.users.personalScope")}</h3>
                <p className="text-xs text-ink-subtle">{t("admin.users.personalScopeHint")}</p>
              </div>
              <ScopeRulesEditor
                rules={draft.scope_rules}
                queues={queues.data ?? []}
                onChange={(scope_rules: ScopeRule[]) => setDraft({ ...draft, scope_rules })}
                emptyText={t("common.none")}
              />
            </section>
          </fieldset>
        </div>
      )}
    </Dialog>
  );
}

export function UsersPage() {
  const { t } = useTranslation();
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.users });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [password, setPassword] = useState<{ title: string; value: string } | null>(null);

  return (
    <>
      <PageHeader
        title={t("admin.users.title")}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden="true" />
            {t("admin.users.new")}
          </Button>
        }
      />
      <Card className="overflow-hidden">
        <div className="relative overflow-x-auto">
          <table className="stack-table w-full min-w-[40rem] text-left text-sm">
            <thead className="border-b border-line bg-surface-2 text-xs text-ink-muted">
              <tr>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.users.user")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.users.roles")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.users.status")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.users.twoFactor")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.users.lastLogin")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  <span className="sr-only">{t("common.edit")}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {users.data?.map((user) => (
                <tr key={user.id} className="hover:bg-surface-2">
                  <td className="px-5 py-3">
                    <div className="font-medium text-ink">{user.display_name}</div>
                    <div className="text-xs text-ink-subtle">
                      {user.username}
                      {user.email ? ` — ${user.email}` : ""}
                    </div>
                  </td>
                  <td className="px-5 py-3" data-label={t("admin.users.roles")}>
                    <div className="flex flex-wrap gap-1.5">
                      {user.roles.map((role) => (
                        <Badge key={role.id} tone={role.is_system ? "accent" : "neutral"}>
                          {role.name}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3" data-label={t("admin.users.status")}>
                    <StatusBadges user={user} />
                  </td>
                  <td className="px-5 py-3 text-ink-muted" data-label={t("admin.users.twoFactor")}>
                    {user.totp_enabled ? t("common.yes") : t("common.no")}
                  </td>
                  <td
                    className="px-5 py-3 whitespace-nowrap text-ink-muted tabular-nums"
                    data-label={t("admin.users.lastLogin")}
                  >
                    {user.last_login_at ? formatDateTime(user.last_login_at) : t("admin.users.never")}
                  </td>
                  <td className="px-5 py-3 text-right max-sm:pt-2">
                    <Button
                      variant="ghost"
                      className="max-sm:-ml-3"
                      onClick={() => setEditing(user.id)}
                      aria-label={`${t("common.edit")} ${user.username}`}
                    >
                      {t("common.edit")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.data?.length === 0 && (
            <p className="px-5 py-6 text-sm text-ink-muted">{t("admin.users.empty")}</p>
          )}
        </div>
      </Card>
      <CreateUserDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(value) => {
          setCreating(false);
          setPassword({ title: t("admin.users.createdTitle"), value });
        }}
      />
      {/* A fresh dialog for every user opened: nothing carries over from the previous one. */}
      <EditUserDialog
        key={editing ?? "closed"}
        userId={editing}
        onClose={() => setEditing(null)}
        onPassword={(value) => setPassword({ title: t("admin.users.resetPassword"), value })}
      />
      <TemporaryPasswordDialog
        title={password?.title ?? ""}
        password={password?.value ?? null}
        onClose={() => setPassword(null)}
      />
    </>
  );
}
